var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { Box, FormHelperText, styled, Typography } from '@mui/material';
import { InputHelperText, Labeled, LinearProgress, useTranslate, } from 'react-admin';
import { useTreeInputController, } from './controllers/useTreeInputController';
import { Tree } from './Tree';
/**
 * The `<TreeInput>` component allows to select one or several nodes from a tree. It expects a `treeData` prop in the format returned by `getTree`.
 *
 * @example
 * ```tsx
 * import { TreeInput } from '@react-admin/ra-tree';
 * import { SimpleForm } from 'react-admin';
 * import treeData from './treeData';
 *
 * export const SimpleTreeForm = () => (
 *     <SimpleForm>
 *         <TreeInput source="category" treeData={treeData} />
 *     </SimpleForm>
 * );
 * ```
 */
export var TreeInput = function (props) {
    var label = props.label, multiple = props.multiple, sx = props.sx, className = props.className, helperText = props.helperText, fullWidth = props.fullWidth, rest = __rest(props, ["label", "multiple", "sx", "className", "helperText", "fullWidth"]);
    var _a = useTreeInputController(props), resource = _a.resource, source = _a.source, tree = _a.tree, fetchError = _a.fetchError, isLoading = _a.isLoading, isRequired = _a.isRequired, field = _a.field, fieldState = _a.fieldState, formState = _a.formState, defaultValue = _a.defaultValue;
    var isTouched = fieldState.isTouched, invalid = fieldState.invalid, error = fieldState.error;
    var isSubmitted = formState.isSubmitted;
    var translate = useTranslate();
    if (fetchError) {
        return (React.createElement(Root, { className: className, sx: sx, "data-testid": "error" },
            React.createElement(Labeled, { label: label, source: source, resource: resource, fullWidth: fullWidth },
                React.createElement(Typography, { color: "error.main" }, translate('ra-tree.treeSelectInput.fetchError', {
                    _: 'Error fetching tree data',
                })))));
    }
    if (isLoading || !tree) {
        return (React.createElement(Root, { className: className, sx: sx, "data-testid": "loading" },
            React.createElement(Labeled, { label: label, source: source, resource: resource, fullWidth: fullWidth },
                React.createElement(LinearProgress, null))));
    }
    return (React.createElement(Root, { className: className, sx: sx },
        React.createElement(Labeled, { htmlFor: source, className: TreeInputClasses.label, color: (isTouched || isSubmitted) && invalid ? 'error' : undefined, label: label, source: source, resource: resource, isRequired: isRequired, fullWidth: fullWidth },
            React.createElement(TreeInputTree, __assign({ source: source, multiple: multiple, className: TreeInputClasses.tree, field: field, defaultValue: defaultValue, fullWidth: fullWidth }, rest, { treeData: tree }))),
        !!((isTouched || isSubmitted) && !!error) || helperText ? (React.createElement(FormHelperText, { error: (isTouched || isSubmitted) && !!error },
            React.createElement(InputHelperText, { touched: isTouched || isSubmitted, error: error === null || error === void 0 ? void 0 : error.message, helperText: helperText }))) : null));
};
var PREFIX = 'RaTreeInput';
export var TreeInputClasses = {
    label: "".concat(PREFIX, "-label"),
    tree: "".concat(PREFIX, "-tree"),
};
var Root = styled(Box, {
    name: PREFIX,
    overridesResolver: function (props, styles) { return styles.root; },
})(function () { return ({}); });
export var TreeInputTree = function (props) {
    var multiple = props.multiple, treeData = props.treeData, field = props.field, _a = props.checkStrictly, checkStrictly = _a === void 0 ? true : _a, _b = props.expandAction, expandAction = _b === void 0 ? false : _b, rawDefaultValue = props.defaultValue, rest = __rest(props, ["multiple", "treeData", "field", "checkStrictly", "expandAction", "defaultValue"]);
    var onBlur = field.onBlur, onChange = field.onChange, rawValue = field.value;
    var value = rawValue || (multiple ? [] : '');
    var defaultValue = rawDefaultValue || (multiple ? [] : '');
    var handleCheck = function (keys, info) {
        var selectedNodes = info.checkedNodes.filter(function (node) {
            return checkStrictly
                ? keys.checked.includes(node.key)
                : keys.includes(node.key);
        });
        var value = selectedNodes.map(function (node) { return node.id; });
        onChange(value);
    };
    var handleSelect = function (keys, info) {
        var value = keys && keys.length > 0
            ? info.node.id
            : '';
        onChange(value);
    };
    return (React.createElement(Tree, __assign({}, rest, { checkStrictly: checkStrictly, expandAction: expandAction, checkable: multiple, selectable: !multiple, checkedKeys: multiple ? value.map(String) : [], selectedKeys: multiple ? [] : [String(value)], defaultCheckedKeys: multiple ? defaultValue.map(String) : undefined, defaultSelectedKeys: multiple ? undefined : [String(defaultValue)], defaultExpandedKeys: multiple ? defaultValue.map(String) : [String(defaultValue)], onCheck: handleCheck, onSelect: handleSelect, treeData: treeData, onBlur: onBlur })));
};
